import React, { useEffect, useMemo, useRef, useState } from "react";
import "./RsvpDiceComponent.scss";
import ButtonComponent from "../ButtonComponent/ButtonComponent";

const GOLD = "#d4af37";
const GOLD_DARK = "#b38728";
const pick = (n) => Math.floor(Math.random() * n); // 0..n-1

const RsvpDiceComponent = ({
  title = "Roll the Dice",
  to = "/rsvp",
  onReveal, // (combo: {a,b,sum,isDouble}) => void
  messages = {
    "1-1": "You've rolled a double 1! Time to RSVP!",
    "2-4": "You've rolled a perfect 6! We’re ready to celebrate—RSVP now!",
    "3-3": "Looks like you’re all set! Let's make it official—RSVP!",
    "6-6": "You’ve hit the jackpot! It’s a 6-star celebration — RSVP now!",
    double: "Doubles! It’s meant to be — RSVP below.",
    sum7: "Lucky seven! That’s a sign — RSVP below.",
    default: "Close enough — destiny says RSVP!",
  },
  rollDuration = 1100, // ms
  allowUnlimited = true,
}) => {
  const [a, setA] = useState(1);
  const [b, setB] = useState(1);
  const [rolling, setRolling] = useState(false);
  const [revealed, setRevealed] = useState(false);
  const [rolledOnce, setRolledOnce] = useState(false);
  const [msg, setMsg] = useState("Click roll to see your fate…");
  const canvasRef = useRef(null);
  const stageRef = useRef(null);
  const audioRef = useRef(null);
  const tickerRef = useRef(null);

  // resize confetti canvas to stage
  useEffect(() => {
    const resize = () => {
      const canvas = canvasRef.current, stage = stageRef.current;
      if (!canvas || !stage) return;
      const r = stage.getBoundingClientRect();
      const dpr = window.devicePixelRatio || 1;
      canvas.width = Math.floor(r.width * dpr);
      canvas.height = Math.floor(r.height * dpr);
      canvas.style.width = r.width + "px";
      canvas.style.height = r.height + "px";
      const ctx = canvas.getContext("2d");
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
      ctx.clearRect(0, 0, r.width, r.height);
    };
    resize();
    const ro = new ResizeObserver(resize);
    if (stageRef.current) ro.observe(stageRef.current);
    window.addEventListener("resize", resize);
    return () => { ro.disconnect(); window.removeEventListener("resize", resize); };
  }, []);

  const playRoll = () => {
    try {
      const AC = window.AudioContext || window.webkitAudioContext;
      if (!AC) return;
      const ctx = (audioRef.current ||= new AC());
      const t0 = ctx.currentTime;

      // soft rolling noise (brown noise-ish)
      const buf = ctx.createBuffer(1, ctx.sampleRate * 0.3, ctx.sampleRate);
      const data = buf.getChannelData(0);
      let last = 0; for (let i = 0; i < data.length; i++) {
        const white = Math.random() * 2 - 1;
        data[i] = (last + 0.02 * white) / 1.02;
        last = data[i];
        data[i] *= 0.8 * (1 - i / data.length);
      }
      const src = ctx.createBufferSource(); src.buffer = buf;
      const g = ctx.createGain(); g.gain.setValueAtTime(0.0001, t0); g.gain.exponentialRampToValueAtTime(0.35, t0 + 0.02);
      g.gain.exponentialRampToValueAtTime(0.0001, t0 + 0.28);
      src.connect(g).connect(ctx.destination); src.start(t0);

      // final "clack"
      const osc = ctx.createOscillator(); const og = ctx.createGain();
      osc.type = "triangle";
      osc.frequency.setValueAtTime(220, t0 + 0.25);
      osc.frequency.exponentialRampToValueAtTime(140, t0 + 0.37);
      og.gain.setValueAtTime(0.0001, t0 + 0.25);
      og.gain.exponentialRampToValueAtTime(0.45, t0 + 0.27);
      og.gain.exponentialRampToValueAtTime(0.0001, t0 + 0.42);
      osc.connect(og).connect(ctx.destination); osc.start(t0 + 0.25); osc.stop(t0 + 0.45);
    } catch {/* ignore */ }
  };

  const confetti = () => {
    const canvas = canvasRef.current; const stage = stageRef.current;
    if (!canvas || !stage) return;
    const ctx = canvas.getContext("2d");
    const rect = stage.getBoundingClientRect();
    const cx = rect.width / 2, cy = rect.height / 2;

    const colors = [GOLD, "#ffffff", GOLD_DARK];
    const pieces = Array.from({ length: 70 }).map(() => ({
      x: cx, y: cy,
      vx: (Math.random() - 0.5) * 5,
      vy: -Math.random() * 4 - 1,
      g: 0.08 + Math.random() * 0.06,
      s: 2 + Math.random() * 3.5,
      r: Math.random() * Math.PI * 2,
      vr: (Math.random() - 0.5) * 0.4,
      c: colors[pick(colors.length)],
      born: performance.now(),
      life: 700 + Math.random() * 700
    }));

    let raf;
    const draw = (now) => {
      ctx.clearRect(0, 0, rect.width, rect.height);
      let alive = 0;
      for (const p of pieces) {
        const t = now - p.born;
        if (t > p.life) continue;
        alive++;
        p.vy += p.g;
        p.x += p.vx; p.y += p.vy; p.r += p.vr;
        ctx.save(); ctx.translate(p.x, p.y); ctx.rotate(p.r);
        ctx.fillStyle = p.c; ctx.fillRect(-p.s / 2, -p.s / 2, p.s, p.s * 1.6);
        ctx.restore();
      }
      if (alive) raf = requestAnimationFrame(draw);
      else ctx.clearRect(0, 0, rect.width, rect.height);
    };
    raf = requestAnimationFrame(draw);
    setTimeout(() => cancelAnimationFrame(raf), 1600);
  };

  const comboMessage = (x, y) => {
    const key = `${x}-${y}`;
    if (messages[key]) return messages[key];
    if (x === y) return messages.double;
    if (x + y === 7) return messages.sum7;
    return messages.default;
  };

  const roll = () => {
    if (rolling) return;
    setRolling(true);
    setRolling(true);

    // Always hide RSVP + update message on every new roll
    if (tickerRef.current) clearInterval(tickerRef.current);
    setRevealed(false);
    setMsg("Rolling…");

    playRoll();

    // quick tick randomization
    let t = 0;
    tickerRef.current = setInterval(() => {
      t += 80;
      setA(1 + pick(6)); setB(1 + pick(6));
      if (t >= rollDuration - 200) { clearInterval(tickerRef.current); }
    }, 80);

    // final result
    setTimeout(() => {
      const finalA = 1 + pick(6);
      const finalB = 1 + pick(6);
      setA(finalA); setB(finalB);

      const m = comboMessage(finalA, finalB);
      setMsg(m);
      setRevealed(true);
      confetti();
      onReveal && onReveal({ a: finalA, b: finalB, sum: finalA + finalB, isDouble: finalA === finalB });

      setRolling(false);
    }, rollDuration);
  };

  // helper to render pip positions for a value 1..6
  const pipPos = useMemo(() => ({
    1: [[2, 2]],
    2: [[1, 1], [3, 3]],
    3: [[1, 1], [2, 2], [3, 3]],
    4: [[1, 1], [1, 3], [3, 1], [3, 3]],
    5: [[1, 1], [1, 3], [2, 2], [3, 1], [3, 3]],
    6: [[1, 1], [1, 3], [2, 1], [2, 3], [3, 1], [3, 3]],
  }), []);

  const Dice = ({ value, spinning }) => (
    <div className={`die ${spinning ? "is-rolling" : ""}`}>
      <div className="die__grid">
        {pipPos[value].map(([r, c], i) => (
          <span key={i} className="pip" style={{ gridRow: r, gridColumn: c }} />
        ))}
      </div>
    </div>
  );

  return (
    <section className="rdice" aria-label="Roll the Dice — RSVP">
      <div className="rdice__card">
        <h3 className="rdice__title">{title}</h3>

        {/* Stage */}
        <div className="rdice__stage" ref={stageRef}>
          <canvas ref={canvasRef} className="rdice__confetti" />
          <div className="rdice__dice">
            <Dice value={a} spinning={rolling} />
            <Dice value={b} spinning={rolling} />
          </div>

          <div className="rdice__controls">
            <ButtonComponent onClick={roll} disabled={rolling}>
  {rolling ? "Rolling…" : revealed ? "Roll again" : "Roll"}
</ButtonComponent>

          </div>

        </div>

        {/* Message + RSVP */}
        <p className={`rdice__message ${revealed ? "is-revealed" : ""}`}>
          {revealed ? msg : " "}
        </p>

        {revealed && (
          <div className="rdice__cta" aria-live="polite">
            <ButtonComponent as="a" href={to}>RSVP Now</ButtonComponent>
          </div>
        )}
      </div>
    </section>
  );
};

export default RsvpDiceComponent;
